/* eslint-disable no-restricted-syntax */
import {
  OFFLINE_MAPS_SOURCE,
  OFFLINE_OVERLAY_SOURCE,
  RANGE_RADIUS_SOURCE,
  HIGHLIGHT_SOURCE,
  ELEVATION_POINT_SOURCE,
  MARKUP_EDIT_SOURCE,
  ALL_CUSTOM_SOURCES,
  PROMOTE_IDS,
  WAYPOINTS_SOURCE,
  WAYPOINTS_LABEL_SOURCE,
  HIGHLIGHTED_WAYPOINTS_SOURCE,
  LINES_SOURCE,
  TRACKS_SOURCE,
  SHAPES_SOURCE,
  ROUTES_SOURCE,
} from '@/lib/enums'
import {
  isWaypointClusteringEnabled,
  getWaypointClusteringParams,
  getClusterIconColors,
} from '@/lib/waypoint-clustering'
import {
  createEditMarkupStyles,
  createElevationPointStyle,
  createHighlightStyles,
  createOfflineOverlayStyle,
  createOfflineStyle,
  createRangeRadiusStyle,
  createSkyStyleLayer,
  createWindMarkerStyle,
  createHighlightedWindMarkerStyle,
  createWaypointStyles,
  createWaypointLabelStyles,
  createHlWaypointStyles,
  createLineStyles,
  createShapeStyles,
} from '@/lib/mapbox/markup-style-factory'

/**
 * Applies all of our client side stylesheet modifications.  This includes
 * custom sources and layers.
 *
 * Updates stylesheet by reference.
 * @param {any} stylesheet
 */
export const addClientStylesheetModifications = (stylesheet) => {
  // Add runtime styles/layers/sources
  addCustomSources(stylesheet)
  addCustomLayers(stylesheet)
  addFog(stylesheet)
}

/**
 * Adds runtime webmap sources to stylesheet.  Updates stylesheet by reference.
 * @param {Object<string, any>} stylesheet
 */
const addCustomSources = (stylesheet) => {
  // iterate through user content sources
  for (const customSourceId of ALL_CUSTOM_SOURCES) {
    stylesheet.sources[customSourceId] = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
      maxzoom: 14,
    }

    const promoteId = PROMOTE_IDS[customSourceId]
    if (promoteId) {
      stylesheet.sources[customSourceId]['promoteId'] = promoteId
    }

    if (
      isWaypointClusteringEnabled.value &&
      customSourceId === WAYPOINTS_SOURCE
    ) {
      const params = getWaypointClusteringParams()
      stylesheet.sources[customSourceId].cluster = false
      stylesheet.sources[customSourceId].clusterMaxZoom = params.maxZoom
      stylesheet.sources[customSourceId].clusterRadius = params.radius
      stylesheet.sources[customSourceId].clusterMinPoints = params.minPoints
    }
  }
}

/**
 * Adds custom map layers to a Mapbox GL JS stylesheet in a specific rendering order.
 *
 * Creates and configures various map layers including waypoints, lines, shapes, tracks,
 * routes, offline maps, highlights, and other interactive elements. Layers are added
 * in a carefully ordered sequence from bottom to top to ensure proper visual hierarchy
 * and user interaction.
 *
 * @param {Object} stylesheet - The Mapbox GL JS style object to modify. stylesheet.layers array of layer objects that will be extended with custom layers
 * @returns {void} - Modifies the stylesheet object in place
 */
const addCustomLayers = (stylesheet) => {
  const skyStyleLayer = createSkyStyleLayer()
  const waypointLayer = createWaypointStyles({
    source: WAYPOINTS_SOURCE,
    ...getClusterIconColors('fish'),
  })
  const waypointLabelLayer = createWaypointLabelStyles({
    source: WAYPOINTS_LABEL_SOURCE,
  })
  const highlightedWaypointLayer = createHlWaypointStyles({
    source: HIGHLIGHTED_WAYPOINTS_SOURCE,
  })
  const lineLayer = createLineStyles({ source: LINES_SOURCE })
  const trackLayer = createLineStyles({ source: TRACKS_SOURCE })
  const shapeLayer = createShapeStyles({ source: SHAPES_SOURCE })
  const routesLayer = createLineStyles({ source: ROUTES_SOURCE })
  const offlineStyleLayer = createOfflineStyle({ source: OFFLINE_MAPS_SOURCE })
  const offlineOverlayLayer = createOfflineOverlayStyle({
    source: OFFLINE_OVERLAY_SOURCE,
  })
  const rangeRadiusStyleLayer = createRangeRadiusStyle({
    source: RANGE_RADIUS_SOURCE,
  })
  const highlightStyleLayer = createHighlightStyles({
    source: HIGHLIGHT_SOURCE,
  })
  const elevationPointLayer = createElevationPointStyle({
    source: ELEVATION_POINT_SOURCE,
  })
  const editMarkupStyles = createEditMarkupStyles({
    source: MARKUP_EDIT_SOURCE,
  })
  const windMarkerStyleLayer = createWindMarkerStyle({
    source: WAYPOINTS_SOURCE,
  })
  const highlightedWindMarkerStyleLayer = createHighlightedWindMarkerStyle({
    source: HIGHLIGHTED_WAYPOINTS_SOURCE,
  })

  // The draft offline overlay has a dimming fill (outside the draft box) and a
  // border + dimension labels. Split them so the fill stays below user content
  // (waypoints/tracks/etc. remain visible inside and outside the draft area)
  // while the border and labels render above user content — otherwise any
  // overlapping shape/track/line/waypoint visually covers the boundary (FOX-13360).
  const offlineOverlayFillLayer = offlineOverlayLayer.filter(
    ({ id }) => id === 'offline-overlay-fill'
  )
  const offlineOverlayBorderLayer = offlineOverlayLayer.filter(
    ({ id }) => id !== 'offline-overlay-fill'
  )

  // IMPORTANT: Verify layer rendering order before making changes to ensure proper visual hierarchy
  // Current layer ordering (bottom to top):
  // 1. Range radius layers (background elements)
  // 2. Land area layers (background elements)
  // 3. Offline overlay fill (dims area outside draft box; stays below content so waypoints/tracks remain visible)
  // 4. Highlight style layers
  // 5. Highlighted wind marker layers
  // 6. Sky style layer
  // 9. Shape layers
  // 10. Track layers
  // 11. Line layers
  // 12. Routes layers
  // 13. Wind marker layers
  // 14. Waypoint layers
  // 15. Waypoint label layers
  // 16. Highlighted waypoint layers
  // 17. Edit markup layers
  // 18. Elevation point label layers
  // 19. Offline style layers (saved offline-region borders, above content so they're never obscured)
  // 20. Offline overlay border + dimension labels (draft offline-map border, above content so it's never obscured)
  const styleLayers = []
  styleLayers.unshift(...rangeRadiusStyleLayer)
  styleLayers.push(...offlineOverlayFillLayer)
  styleLayers.push(...highlightStyleLayer)
  styleLayers.push(...highlightedWindMarkerStyleLayer)
  styleLayers.push(skyStyleLayer)
  styleLayers.push(...shapeLayer)
  styleLayers.push(...trackLayer)
  styleLayers.push(...lineLayer)
  styleLayers.push(...routesLayer)
  styleLayers.push(...windMarkerStyleLayer)
  styleLayers.push(...waypointLayer)
  styleLayers.push(...waypointLabelLayer)
  styleLayers.push(...highlightedWaypointLayer)
  styleLayers.push(...editMarkupStyles)
  styleLayers.push(...elevationPointLayer)
  styleLayers.push(...offlineStyleLayer)
  styleLayers.push(...offlineOverlayBorderLayer)

  // add new layers
  stylesheet.layers.push(...styleLayers)
}

/**
 * Adds fog to stylesheet.  Updates stylesheet by reference.
 * @param {object} stylesheet
 */
const addFog = (stylesheet) => {
  if (!stylesheet.fog) {
    stylesheet.fog = {
      range: [-1, 20],
      color: 'white',
      'horizon-blend': 0.05,
    }
  }
}
